<!--

============================================================================================================
||    TimeFrequency component                  - Check DurationWrapper during rewrite                     ||
============================================================================================================

|   Description
------------------------------------------------------------------------------------------------------------
  Component enables to input time frequency in minutes, hours or days providing manual number input and dropdown for time scope.
  As output it gives validation, time in seconds, time input and time scope values.

|   Features
------------------------------------------------------------------------------------------------------------
  - Validation - Both fields are required and time input has to be in range of 5 minutes to 30 days by default or as set via props.

|   Props
------------------------------------------------------------------------------------------------------------
  - value             : Object    : Used for two-way binding. Contains valid, timeInput, timeScope and seconds.
  - minSeconds        : Number    : Minimum seconds value. Default 300.
  - minSecondsMessage : String    : Error message for minimum seconds value. Default 'Retrigger period can\'t be less than 5 minutes.'
  - maxSeconds        : Number    : Maximum seconds value. Default 30 days.
  - maxSecondsMessage : String    : Error message for maximum seconds value. Default 'Retrigger period can\'t exceed 30 days.'

|   Events
------------------------------------------------------------------------------------------------------------
  - input       : Object    : Emits object with valid, timeInput, timeScope and seconds values.

|   v-model
------------------------------------------------------------------------------------------------------------
  {
    valid: [Boolean],
    timeInput: [Number],
    timeScope: [Object],
    seconds: [Number],
  }

|   TODO
------------------------------------------------------------------------------------------------------------
  - [min/max]SecondsMessage - Find highest possible scope like in setInitialValues method and set the default messages. If user overrides it then ignore the default messages.

-->

<template>
  <ValidationObserver
    ref="timeFrequencyObserver"
    v-slot="{ errors }" >
    <div class="time-frequency flex center-all gap-10">
      <ValidationProvider
        name="Time"
        class="w-100"
        :rules="{
          required: true,
        }" >
        <input
          ref="timeInputRef"
          v-model="timeInput"
          type="number"
          min="0"
          class="time-input sb2-bordered sb2-hoverable sb2-focusable mr-2 w-100"
          :placeholder="`Enter ${timeScope.name ? timeScope.name.toLowerCase() : ''}`" >
      </ValidationProvider>
      <multiselect
        v-model="timeScope"
        v-select-overflow
        placeholder="Select"
        class="flex-options hide-tags no-selected-background"
        :options="timeScopeTypes"
        label="name"
        track-by="name"
        :multiple="false"
        :show-labels="false"
        :show-no-results="false"
        :close-on-select="true"
        :clear-on-select="false"
        :preserve-search="true"
        :allow-empty="false"
        :searchable="false"
        preselect-first >
        <template
          slot="option"
          slot-scope="props">
          <SBIcon
            :icon="SBIconList.DONE_TICK"
            :color="SBColors.bluePrimary"
            :size="9"/>
          <span class="ellipsis">
            {{ props.option.name }}
          </span>
        </template>
      </multiselect>
    </div>
    <div
      v-if="error || (errors.Time && errors.Time.length)"
      class="error flex flex-align-center">
      <SBIcon
        class="mr-2"
        :icon="SBIconList.ERROR"
        :color="SBColors.redPrimary"
        :size="12" />
      <span>{{ error || errors.Time[0] }}</span>
    </div>
  </ValidationObserver>
</template>

<script>
import Multiselect from 'vue-multiselect';
import SBColors from '@/helpers/lists/colors';
import SBIcon from '@/components/common/Icon';
import SBIconList from '@/helpers/lists/sb-icon-list';

export default {
  name: 'TimeFrequency',
  components: {
    Multiselect,
    SBIcon,
  },
  props: {
    value: {
      type: Object,
      default: () => {},
    },
    minSeconds: {
      type: Number,
      default: 300,
    },
    minSecondsMessage: {
      type: String,
      default: 'Retrigger period can\'t be less than 5 minutes.',
    },
    maxSeconds: {
      type: Number,
      default: 30 * 24 * 60 * 60, // 30 days
    },
    maxSecondsMessage: {
      type: String,
      default: 'Retrigger period can\'t exceed 30 days.',
    },
  },
  data() {
    return {
      timeInput: '',
      timeScope: '',
      timeScopeTypes: [
        {
          name: 'Minutes',
          factor: 60,
        },
        {
          name: 'Hours',
          factor: 3600,
        },
        {
          name: 'Days',
          factor: 86400,
        },
      ],
      preventInitialChange: false,
      error: null,
      SBIconList,
      SBColors,
    };
  },
  watch: {
    timeInput() {
      this.emit();
    },
    timeScope() {
      this.emit();
    },
    value: {
      handler() {
        if (this.preventInitialChange) {
          return;
        }
        this.preventInitialChange = true;
        this.setInitialValues();
      },
      deep: true,
    },
  },
  mounted() {
    this.setInitialValues();
  },
  methods: {
    focus() {
      this.$refs.timeInputRef.focus();
    },
    /**
     * Sets initial values for time input and time scope from value.
     * It finds highest possible scope for given seconds value.
     */
    setInitialValues() {
      this.timeInput = this.value && this.value.seconds ? this.value.seconds / this.timeScopeTypes[0].factor : this.minSeconds / this.timeScopeTypes[0].factor;
      this.timeScope = this.timeScopeTypes[0];

      this.timeScopeTypes.slice().reverse().some((scope) => {
        const seconds = this.value && this.value.seconds ? this.value.seconds : this.minSeconds;
        if (seconds % scope.factor === 0 && !(scope.factor === seconds && seconds === 86400)) {
          this.timeInput = seconds / scope.factor;
          this.timeScope = scope;
          return true;
        }
        return false;
      });

      this.$nextTick(() => this.emit());
    },
    /**
     * Validates if time input is in range of minSeconds and maxSeconds.
     * @returns {Boolean} - True if time input is in range, false otherwise.
     */
    validSecondsRange() {
      if (this.timeInput * this.timeScope.factor > this.maxSeconds) {
        this.error = this.maxSecondsMessage;
      } else if (this.timeInput * this.timeScope.factor < this.minSeconds) {
        this.error = this.minSecondsMessage;
      } else {
        this.error = null;
      }
      return this.error === null;
    },
    /**
     * Emits input event.
     */
    emit() {
      if (!this.$refs.timeFrequencyObserver) return;
      this.$refs.timeFrequencyObserver.validate()
        .then((valid) => {
          this.$emit('input', {
            valid: valid && this.validSecondsRange(),
            timeInput: this.timeInput,
            timeScope: this.timeScope,
            seconds: this.timeInput * this.timeScope.factor,
          });
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.time-frequency {
  .time-input {
    font-size: 14px;
    padding-top: 6px;
  }
}

.error {
  font-size: 14px;
  color: $redPrimary;
  margin-bottom: 3px;
}
</style>
